import { useEffect, useRef, useState } from 'react';
import './GenerationProgress.css';

const R = 52;
const CIRC = 2 * Math.PI * R;

// The backend reports no progress, so the ring follows the expected duration and parks at 95%.
export function GenerationProgress({ durationMs, title, messages, tip }) {
    const startRef = useRef(Date.now());
    const wrapRef = useRef(null);
    const [now, setNow] = useState(() => Date.now());
    const [msgIdx, setMsgIdx] = useState(0);

    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 300);
        const rotate = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 3500);
        const scroll = setTimeout(() => wrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        return () => { clearInterval(tick); clearInterval(rotate); clearTimeout(scroll); };
    }, [messages.length]);

    const elapsed = now - startRef.current;
    const progress = Math.min(95, (elapsed / durationMs) * 100);
    const secsLeft = Math.max(0, Math.round((durationMs - elapsed) / 1000));

    return (
        <div className="genProgress glass" ref={wrapRef} role="status" aria-live="polite">
            {title && (
                <div className="heroPill"><span className="dot dot--pulse" /> Now rendering: <strong>{title}</strong></div>
            )}
            <div className="genRing">
                <svg viewBox="0 0 120 120" className="genRingSvg" aria-hidden="true">
                    <defs>
                        <linearGradient id="genRingGrad" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0" stopColor="var(--brand-500)" />
                            <stop offset="1" stopColor="var(--sky-500)" />
                        </linearGradient>
                    </defs>
                    <circle className="genRingTrack" cx="60" cy="60" r={R} />
                    <circle
                        className="genRingBar"
                        cx="60" cy="60" r={R}
                        stroke="url(#genRingGrad)"
                        strokeDasharray={CIRC}
                        strokeDashoffset={CIRC * (1 - progress / 100)}
                    />
                </svg>
                <div className="genRingLabel">
                    <span className="genRingPct">{Math.round(progress)}<small>%</small></span>
                </div>
            </div>
            <div className="genProgressMsg" key={msgIdx}>{messages[msgIdx]}…</div>
            <div className="genProgressTime">{secsLeft > 0 ? `~${secsLeft}s remaining` : 'Finishing up'}</div>
            {tip && <div className="genProgressTip">{tip}</div>}
        </div>
    );
}
